import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from '../components/ui/dialog';
import { FileSpreadsheet, FileText, Filter, AlertCircle, Mail, Send } from 'lucide-react';
import { formatCurrency } from '../utils/formatCurrency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Reports = () => {
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [summary, setSummary] = useState({ count: 0, total_amount: 0, total_paid: 0, total_difference: 0 });
  
  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState('all');

  // Send to Accountant Dialog
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [includeInvoices, setIncludeInvoices] = useState(true);
  const [accountantEmail, setAccountantEmail] = useState('steuerberater@kaufmanns-consulting.com');

  const statusConfig = {
    draft: { label: t('invoices.statusDraft'), color: 'bg-slate-100 text-slate-600 border-slate-200' },
    sent: { label: t('invoices.statusSent'), color: 'bg-blue-50 text-blue-600 border-blue-200' },
    paid: { label: t('invoices.statusPaid'), color: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
    overdue: { label: t('invoices.statusOverdue'), color: 'bg-amber-50 text-amber-600 border-amber-200' },
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    fetchReportData();
  }, [startDate, endDate, statusFilter, customerFilter]);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error('Error loading customers');
    }
  };

  const fetchReportData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (customerFilter !== 'all') params.append('customer_id', customerFilter);
      
      const response = await axios.get(`${API}/reports/invoices?${params.toString()}`);
      setInvoices(response.data.invoices);
      setSummary(response.data.summary);
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setExporting(true);
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (customerFilter !== 'all') params.append('customer_id', customerFilter);
      
      const response = await axios.get(`${API}/reports/invoices/excel?${params.toString()}`, {
        responseType: 'blob',
        timeout: 30000
      });
      
      // Check if response is actually a blob/file
      if (response.data.size === 0) {
        throw new Error('Empty file received');
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Rechnungsbericht_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(t('reports.exportSuccess'));
    } catch (error) {
      console.error('Excel export error:', error);
      const errorMsg = error.response?.data?.detail || error.message || t('reports.exportError');
      toast.error(errorMsg);
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      setExporting(true);
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (customerFilter !== 'all') params.append('customer_id', customerFilter);
      
      const response = await axios.get(`${API}/reports/invoices/pdf?${params.toString()}`, {
        responseType: 'blob',
        timeout: 30000
      });
      
      // Check if response is actually a blob/file
      if (response.data.size === 0) {
        throw new Error('Empty file received');
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data], { 
        type: 'application/pdf' 
      }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Rechnungsbericht_${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(t('reports.exportSuccess'));
    } catch (error) {
      console.error('PDF export error:', error);
      const errorMsg = error.response?.data?.detail || error.message || t('reports.exportError');
      toast.error(errorMsg);
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE');
  };

  const handleSendToAccountant = async () => {
    try {
      setSendingReport(true);
      const params = new URLSearchParams();
      params.append('month', selectedMonth);
      params.append('year', selectedYear);
      params.append('include_invoices', includeInvoices);
      params.append('accountant_email', accountantEmail);
      
      const response = await axios.post(`${API}/reports/send-to-accountant?${params.toString()}`);
      
      toast.success(t('reports.sendSuccess', { email: accountantEmail }));
      setSendDialogOpen(false);
    } catch (error) {
      console.error('Send report error:', error);
      const errorMsg = error.response?.data?.detail || t('reports.sendError');
      toast.error(errorMsg);
    } finally {
      setSendingReport(false);
    }
  };

  const months = [
    { value: 1, label: t('months.january') },
    { value: 2, label: t('months.february') },
    { value: 3, label: t('months.march') },
    { value: 4, label: t('months.april') },
    { value: 5, label: t('months.may') },
    { value: 6, label: t('months.june') },
    { value: 7, label: t('months.july') },
    { value: 8, label: t('months.august') },
    { value: 9, label: t('months.september') },
    { value: 10, label: t('months.october') },
    { value: 11, label: t('months.november') },
    { value: 12, label: t('months.december') },
  ];

  const years = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear; y >= currentYear - 5; y--) {
    years.push(y);
  }

  const getPaymentDifference = (invoice) => {
    if (invoice.status !== 'paid' || invoice.payment_amount === null || invoice.payment_amount === undefined) {
      return null;
    }
    return invoice.total_amount - invoice.payment_amount;
  };

  return (
    <div className="p-4 sm:p-6 md:p-8 lg:p-12 space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-slate-900">
            {t('reports.title')}
          </h1>
          <p className="text-slate-500 mt-1 text-sm sm:text-base">{t('reports.subtitle')}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <Button 
            onClick={handleExportExcel}
            disabled={exporting || invoices.length === 0}
            className="w-full sm:w-auto rounded-sm bg-emerald-600 hover:bg-emerald-700 shadow-none"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            {t('reports.exportExcel')}
          </Button>
          <Button 
            onClick={handleExportPDF}
            disabled={exporting || invoices.length === 0}
            className="w-full sm:w-auto rounded-sm bg-primary hover:bg-[#D62839] shadow-none"
          >
            <FileText className="w-4 h-4 mr-2" />
            {t('reports.exportPdf')}
          </Button>
          <Button 
            onClick={() => setSendDialogOpen(true)}
            variant="outline"
            className="w-full sm:w-auto rounded-sm border-blue-300 text-blue-600 hover:bg-blue-50 shadow-none"
          >
            <Mail className="w-4 h-4 mr-2" />
            {t('reports.sendToAccountant')}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200 shadow-none rounded-sm">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Filter className="w-4 h-4" />
            {t('common.filter')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>{t('reports.startDate')}</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="rounded-sm"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('reports.endDate')}</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="rounded-sm"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('invoices.status')}</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('invoices.allStatus')}</SelectItem>
                  <SelectItem value="draft">{t('invoices.statusDraft')}</SelectItem>
                  <SelectItem value="sent">{t('invoices.statusSent')}</SelectItem>
                  <SelectItem value="paid">{t('invoices.statusPaid')}</SelectItem>
                  <SelectItem value="overdue">{t('invoices.statusOverdue')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('invoices.customer')}</Label>
              <Select value={customerFilter} onValueChange={setCustomerFilter}>
                <SelectTrigger className="rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('invoices.allCustomers')}</SelectItem>
                  {customers.map((customer) => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.company_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-slate-200 shadow-none rounded-sm">
          <CardContent className="pt-6">
            <p className="text-xs uppercase tracking-wider text-slate-500">{t('reports.invoiceCount')}</p>
            <p className="font-mono text-2xl font-bold text-slate-900">{summary.count}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-none rounded-sm">
          <CardContent className="pt-6">
            <p className="text-xs uppercase tracking-wider text-slate-500">{t('reports.totalAmount')}</p>
            <p className="font-mono text-2xl font-bold text-slate-900">{formatCurrency(summary.total_amount)}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-none rounded-sm">
          <CardContent className="pt-6">
            <p className="text-xs uppercase tracking-wider text-slate-500">{t('reports.totalPaid')}</p>
            <p className="font-mono text-2xl font-bold text-emerald-600">{formatCurrency(summary.total_paid)}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-none rounded-sm">
          <CardContent className="pt-6">
            <p className="text-xs uppercase tracking-wider text-slate-500">{t('reports.totalDifference')}</p>
            <p className={`font-mono text-2xl font-bold ${summary.total_difference !== 0 ? 'text-amber-600' : 'text-slate-400'}`}>
              {summary.total_difference !== 0 ? formatCurrency(summary.total_difference) : '-'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Invoices Table */}
      <Card className="border-slate-200 shadow-none rounded-sm">
        <CardHeader>
          <CardTitle className="text-base">{t('reports.invoiceList')}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-500">{t('common.loading')}</div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-8 text-slate-400">{t('reports.noData')}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-2 font-medium text-slate-600">{t('invoices.invoiceNumber')}</th>
                    <th className="text-left py-3 px-2 font-medium text-slate-600">{t('invoices.customer')}</th>
                    <th className="text-left py-3 px-2 font-medium text-slate-600">{t('invoices.invoiceDate')}</th>
                    <th className="text-left py-3 px-2 font-medium text-slate-600">{t('invoices.status')}</th>
                    <th className="text-right py-3 px-2 font-medium text-slate-600">{t('invoices.amount')}</th>
                    <th className="text-right py-3 px-2 font-medium text-slate-600">{t('reports.paid')}</th>
                    <th className="text-right py-3 px-2 font-medium text-slate-600">{t('reports.difference')}</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => {
                    const difference = getPaymentDifference(invoice);
                    const hasDifference = difference !== null && Math.abs(difference) > 0.01;
                    
                    return (
                      <tr key={invoice.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-2 font-mono text-slate-900">{invoice.invoice_number}</td>
                        <td className="py-3 px-2 text-slate-700">{invoice.customer_data?.company_name}</td>
                        <td className="py-3 px-2 font-mono text-slate-600">{formatDate(invoice.invoice_date)}</td>
                        <td className="py-3 px-2">
                          <Badge className={`rounded-full ${statusConfig[invoice.status]?.color} border text-xs`}>
                            {statusConfig[invoice.status]?.label}
                          </Badge>
                        </td>
                        <td className="py-3 px-2 text-right font-mono text-slate-900">{formatCurrency(invoice.total_amount)}</td>
                        <td className="py-3 px-2 text-right font-mono text-slate-600">
                          {invoice.status === 'paid' && invoice.payment_amount !== null 
                            ? formatCurrency(invoice.payment_amount)
                            : '-'}
                        </td>
                        <td className="py-3 px-2 text-right">
                          {hasDifference && (
                            <div className="flex items-center justify-end gap-1">
                              <AlertCircle className="w-3 h-3 text-amber-500" />
                              <span className="font-mono text-amber-600">{formatCurrency(difference)}</span>
                            </div>
                          )}
                          {!hasDifference && invoice.status === 'paid' && (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment Differences with Reasons */}
      {invoices.some(inv => inv.payment_difference_reason) && (
        <Card className="border-amber-200 bg-amber-50 shadow-none rounded-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base text-amber-800">
              <AlertCircle className="w-4 h-4" />
              {t('reports.paymentDifferences')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {invoices
                .filter(inv => inv.payment_difference_reason)
                .map((invoice) => {
                  const difference = getPaymentDifference(invoice);
                  return (
                    <div key={invoice.id} className="p-3 bg-white rounded-sm border border-amber-200">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="font-mono font-medium text-slate-900">{invoice.invoice_number}</p>
                          <p className="text-sm text-slate-600">{invoice.customer_data?.company_name}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-mono font-bold text-amber-600">{formatCurrency(difference)}</p>
                          <p className="text-xs text-slate-500">{t('reports.difference')}</p>
                        </div>
                      </div>
                      <p className="mt-2 text-sm text-slate-700 bg-slate-50 p-2 rounded">
                        <span className="font-medium">{t('reports.reason')}:</span> {invoice.payment_difference_reason}
                      </p>
                    </div>
                  );
                })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Send to Accountant Dialog */}
      <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
        <DialogContent className="rounded-sm sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <Mail className="w-5 h-5" />
              {t('reports.monthlyReport')}
            </DialogTitle>
            <DialogDescription>
              {t('reports.sendToAccountant')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('reports.selectMonth')}</Label>
                <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(Number(v))}>
                  <SelectTrigger className="rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map((month) => (
                      <SelectItem key={month.value} value={String(month.value)}>
                        {month.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('reports.selectYear')}</Label>
                <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
                  <SelectTrigger className="rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((year) => (
                      <SelectItem key={year} value={String(year)}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('reports.accountantEmail')}</Label>
              <Input
                type="email"
                value={accountantEmail}
                onChange={(e) => setAccountantEmail(e.target.value)}
                placeholder="steuerberater@example.com"
                className="rounded-sm"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="includeInvoices"
                checked={includeInvoices}
                onCheckedChange={setIncludeInvoices}
              />
              <Label htmlFor="includeInvoices" className="cursor-pointer">
                {t('reports.includeInvoices')}
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setSendDialogOpen(false)}
              className="rounded-sm"
            >
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleSendToAccountant}
              disabled={sendingReport || !accountantEmail}
              className="rounded-sm bg-blue-600 hover:bg-blue-700"
            >
              {sendingReport ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  {t('reports.sending')}
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  {t('reports.sendReport')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Reports;
